import {
  getDefaultShipper,
  getDefaultPickupDateTimeInfo,
  getDefaultPickupDateTimeErrorInfo,
  getDefaultShipperError,
} from "./ShipperInfo/utils";
import {
  getDefaultShipment,
  getDefaultShipmentError,
} from "./ShipmentInfo/utils";
import {
  getDefaultEditReasonInfo,
  getDefaultEditReasonErrorInfo,
} from "./EditReasonInfo/utils";
import { getDefaultBillingInfo } from "./Billing/utils";
import { PICKUP_MANAGEMENT_MODES, WARNING_DETAILS } from "./constants";
import { convertDateTimeToTimeString } from "../../../../../dataGrid/gridUtilities";
import { format } from "date-fns";
import { getDataWithQueryString } from "../../../../../../api/componentAPIs";
import getDataWithAbort from "../../../../../../api/getDataWithAbort";
import Label from "./MergePickupShipperDetails/RadioLabel"
import { getRadioLabelData } from "./MergePickupShipperDetails/utils";
const getDefaultPickupManagementInfo = (pickup, terminalOffset) => {
  return {
    shipperInfo: getDefaultShipper(pickup),
    shipmentInfo: pickup
      ? pickup.shipments.map((shipment) => getDefaultShipment(shipment))
      : [],
    pickupDateTimeInfo: getDefaultPickupDateTimeInfo(pickup, terminalOffset),
    specialInstructions: pickup?.specialInstructions ?? "",
    billingInfo: getDefaultBillingInfo(pickup),
    editReasonInfo: getDefaultEditReasonInfo(pickup),
    isSwapPickup: pickup?.isSwapPickup ?? false,
  };
};

const getDefaultPickupManagementErrorInfo = () => {
  return {
    pickupDateTimeInfo: getDefaultPickupDateTimeErrorInfo(),
    shipperInfo: getDefaultShipperError(),
    shipmentInfo: [getDefaultShipmentError()],
    editReasonInfo: getDefaultEditReasonErrorInfo(),
  };
};

const getSavePickupMessage = (mode) => {
  switch (mode) {
    case PICKUP_MANAGEMENT_MODES.CREATE_PICKUP:
      return {
        successMsg: "Pickup Created Successfully",
        errorMsg: "Create Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.EDIT_PICKUP:
      return {
        successMsg: "Pickup Updated Successfully",
        errorMsg: "Edit Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.MERGE_PICKUP:
      return {
        successMsg: "Pickups Merged Successfully",
        errorMsg: "Merge Pickups Failed",
      };
    default:
      return {
        successMsg: "Void Pickup Successful",
        errorMsg: "Void Pickup Failed",
      };
  }
};

const isVoidPickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.VOID_PICKUP === mode;
};

const isMergePickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.MERGE_PICKUP === mode;
}

const isCreatePickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.CREATE_PICKUP === mode;
}

const getConfirmationButtonLabel = (mode,driver) => {
  switch (mode) {
    case PICKUP_MANAGEMENT_MODES.CREATE_PICKUP:
      return "Create Pickup";
    case PICKUP_MANAGEMENT_MODES.EDIT_PICKUP:
      return "Done";
    case PICKUP_MANAGEMENT_MODES.MERGE_PICKUP:
      return driver ? "Merge Pickups & Assign Driver":"Merge Pickups";
    default:
      return "Void Pickup";
  }
};

const checkIsDateTimeUpdated = (pickupData, pickupDateTimeInfo) => {
  const { pickupReadyTime, pickupCloseTime, pickupDate } = pickupData;
  const { readyTime, closeTime } = pickupDateTimeInfo;
  return (
    convertDateTimeToTimeString(readyTime) !== pickupReadyTime ||
    convertDateTimeToTimeString(closeTime) !== pickupCloseTime ||
    pickupDate !== format(pickupDateTimeInfo.pickupDate, "yyyy-MM-dd")
  );
};

const getSelectedTerminalDetails = (selectedTerminal, terminals) => {
  return terminals.find((terminal) => terminal.value === selectedTerminal);
};

const getWarningLabel = (mode) => {
  const {WARNING_LABELS: {CREATE, EDIT, MERGE}} = WARNING_DETAILS;
  const warningLabel = {
    [PICKUP_MANAGEMENT_MODES.CREATE_PICKUP]: CREATE,
    [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP]: EDIT,
    [PICKUP_MANAGEMENT_MODES.MERGE_PICKUP]: MERGE,
  }
  return warningLabel[mode]
}

const getFilteredShipmentsData = (shipmentInfo) => {
  return shipmentInfo.reduce((acc, shipment, index) => {
    if (Object.values(shipment).filter(Boolean).length === 0) {
      return acc;
    }
    return [
      ...acc,
      {
        ...shipment,
        sequenceNumber: (index+1).toString(),
      },
    ];
  }, []);
};

const getFormattedShippers = (pickupData, selectedPossibleMergePickupId) => {
  const selectedPickupId = selectedPossibleMergePickupId?.toLowerCase();
  let newPickupData = pickupData;
  if (selectedPickupId) {
    /* if possible merge pickup icon click on grid select that shipper in 1st position */
    newPickupData = moveSelectedPossibleMergePickupToFirstPosition(
      pickupData,
      selectedPickupId
    );
  }

  return newPickupData.map((pickup) => {
    return {
      label: <Label data={getRadioLabelData(pickup)} />,
      value: JSON.stringify({
        rmaxShipperCode: pickup.rmaxShipperCode,
        pickupId: pickup.pickupId,
      }),
      pickupId: pickup.pickupId
    };
  });
};

const getFormattedTerminals = (terminals) => {
  return terminals.map((terminal) => ({
    label: terminal.terminalCode,
    value: terminal.terminalCode,
    offset: terminal.utcToLocalOffset,
  }));
}

const fetchTerminalAndRestrictionsData = (terminalCode) => {
  return Promise.all([
    getDataWithQueryString(
      "GetAllAppTerminals",
      `terminalCode=${terminalCode}`
    ),
    getDataWithQueryString(
      "PickupRestrictions",
      `terminalCode=${terminalCode}`
    ),
  ]);
};

const getCombinedSpecialInstructions = (specialInstructions) => {
  return specialInstructions.join(" ").trim();
}

const fetchLocationMappingData = ({
  terminalCode,
  zipCode,
}) => {
  return getDataWithAbort(
    "GetLocationMapping",
    `terminalCode=${terminalCode}&zip=${zipCode}`,
  );
};

const getFirstSelectedAssignedDriverPickup = (pickupData) => {
  return pickupData.find(pickup => pickup.driverId);
}

const moveSelectedPossibleMergePickupToFirstPosition = (
  pickupData,
  selectedPickupId
) => {
  const selectedPickupIndex = pickupData.findIndex(
    (pickup) => pickup.pickupId.toLowerCase() === selectedPickupId
  );
  const newPickupData = [...pickupData];
  if (selectedPickupIndex !== -1) {
    const selectedPickup = newPickupData.splice(selectedPickupIndex, 1);
    return [...selectedPickup, ...newPickupData];
  }
  return pickupData;
};

const getShipperByPickupId = (shipperList, pickupId) => {
  if (!pickupId) {
    return null;
  }
  return (
    shipperList.find(
      (shipper) => shipper.pickupId.toLowerCase() === pickupId.toLowerCase()
    )?.value ?? null
  );
};

export {
  getDefaultPickupManagementInfo,
  getDefaultPickupManagementErrorInfo,
  getSavePickupMessage,
  checkIsDateTimeUpdated,
  isVoidPickupMode,
  isMergePickupMode,
  getConfirmationButtonLabel,
  getSelectedTerminalDetails,
  getWarningLabel,
  getFilteredShipmentsData,
  getFormattedShippers,
  getFormattedTerminals,
  fetchTerminalAndRestrictionsData,
  isCreatePickupMode,
  getCombinedSpecialInstructions,
  fetchLocationMappingData,
  getFirstSelectedAssignedDriverPickup,
  getShipperByPickupId,
};
